import isEmpty from 'lodash/isEmpty';
import type { ImageLoader } from 'next/image';
import type { NextRouter } from 'next/router';
import type { FC, ReactElement } from 'react';
import { useMemo } from 'react';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';

import type { CountryCodeEnumType } from '@/types/regionTypes';

import { getUserTitleLabelByValue } from '@/components/FormFields/TitleField';
import { Loader } from '@/components/Loader';
import { Typography } from '@/components/Typography';
import { APOLLO_CLIENTS } from '@/graphql/client';
import type {
  OrderAppDeliveryMode,
  OrderAppOrderStatus,
} from '@/graphql/orderApp/api';
import { useStorefrontWithdrawalEligibilityQuery } from '@/graphql/orderApp/api';
import type {
  FulfillmentFragment,
  LanguageCodeEnum,
  UserFragment,
} from '@/graphql/saleor/api';
import { useFetchGwpName } from '@/hooks/useFetchGwpName';
import { useImageLoader } from '@/hooks/useImageLoader';
import { useIsSmDown } from '@/hooks/useMedia';
import { useOrderIsColorado } from '@/hooks/useOrderIsColorado';
import { usePaymentToken } from '@/hooks/usePaymentToken';
import { useRouter } from '@/hooks/useRouter';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { MyOrdersLayout } from '@/layouts/MyOrdersLayout';
import { BRAND, WITHDRAWAL_ENABLED } from '@/lib/const';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import type { paths } from '@/lib/paths';
import type { PaymentRecord } from '@/lib/payment/types';
import {
  extractBalance,
  extractPreorderData,
  isOrderDepositPaid,
} from '@/lib/preOrder';
import { getTaxMessageShort } from '@/lib/regions';
import * as AM from '@/messages/myAccount';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { OrderDetailsWithdrawalUiState } from '@/views/withdrawal/hooks/useOrderDetailsWithdrawal';
import { useOrderDetailsWithdrawal } from '@/views/withdrawal/hooks/useOrderDetailsWithdrawal';
import { useOrderWithdrawalScope } from '@/views/withdrawal/hooks/useOrderWithdrawalScope';

import { getContactPhone } from '../ReviewView/utils';
import { StoresSearch } from './components/StoresSearch';
import {
  type OrderDetailsGuestSession,
  type OrderDetailsViewProps,
  resolveOrderDetailsDisplayLines,
  sortLines,
  useOrderDetails,
} from './helpers';
import {
  type OrderDetailsContextValue,
  OrderDetailsProvider,
} from './OrderDetailsContext';
import { OrderDetailsSections } from './OrderDetailsSections';

interface ProductNameProps {
  channel: string;
  id: string | undefined;
  index: number;
  languageCode: LanguageCodeEnum;
  productName: string;
  productType: string | undefined;
}

export const ProductName: FC<ProductNameProps> = ({
  channel,
  id,
  index,
  languageCode,
  productName,
  productType,
}) => {
  const translatedProductName = useFetchGwpName(
    languageCode,
    id,
    productType,
    channel,
    productName,
  );

  return (
    <Typography
      data-test-id={`OrderDetailsViewOrderLineProductName-${index}`}
      scale='h6'
    >
      {translatedProductName}
    </Typography>
  );
};

type OrderDetailsLoadedProps = Readonly<{
  IsTypeOrder: ReturnType<typeof useOrderIsColorado>['IsTypeOrder'];
  channelSlug: string;
  deliveryModeTitleTranslation: (mode: OrderAppDeliveryMode) => string;
  formatMessage: IntlShape['formatMessage'];
  guestSession?: OrderDetailsGuestSession;
  imageLoader: ImageLoader;
  isMobileView: boolean;
  isPayPerLinkTokenLoading: boolean | undefined;
  languageCode: LanguageCodeEnum;
  paths: typeof paths;
  payPerLinkToken: string | undefined;
  push: NextRouter['push'];
  state: {
    fulfillment?: FulfillmentFragment;
    loading: boolean;
    order: NonNullable<OrderDetailsViewProps['order']>;
    paymentMethod?: OrderDetailsViewProps['paymentMethod'];
  };
  type?: string;
  user?: Partial<UserFragment> | null;
  withdrawal: OrderDetailsWithdrawalUiState;
}>;

function OrderDetailsLoaded({
  IsTypeOrder,
  channelSlug,
  deliveryModeTitleTranslation,
  formatMessage,
  guestSession,
  imageLoader,
  isMobileView,
  isPayPerLinkTokenLoading,
  languageCode,
  paths,
  payPerLinkToken,
  push,
  state,
  type,
  user,
  withdrawal,
}: OrderDetailsLoadedProps) {
  const resolvedOrder = state.order;
  const { coSalesTax, isColoradoBilling, salesTax } =
    IsTypeOrder(resolvedOrder);

  const fulfilledFulfillment = resolvedOrder.fulfillments?.find(
    (item) => item.status === 'FULFILLED',
  );

  const trackingNumber = fulfilledFulfillment?.trackingNumber;
  const trackingLink = fulfilledFulfillment?.trackingUrl;

  const deliveryMode = resolvedOrder.metadata.find(
    (item) => item.key === 'order.delivery_mode',
  )?.value as OrderAppDeliveryMode;
  const isHomeDelivery = deliveryMode === 'HOME_DELIVERY';
  const deliveryMethodName = deliveryModeTitleTranslation(deliveryMode);

  const shippingTitleValue =
    (resolvedOrder.shippingAddress?.metadata
      ? getMetaValue(
          resolvedOrder.shippingAddress.metadata,
          ACCOUNT_METADATA.personalTitle,
        )
      : undefined) ?? resolvedOrder.shippingTitle;
  const billingTitleValue =
    (resolvedOrder.billingAddress?.metadata
      ? getMetaValue(
          resolvedOrder.billingAddress.metadata,
          ACCOUNT_METADATA.personalTitle,
        )
      : undefined) ?? resolvedOrder.billingTitle;
  const shippingTitleLabel = getUserTitleLabelByValue(
    formatMessage,
    resolvedOrder.shippingAddress?.country.code as
      | CountryCodeEnumType
      | undefined,
    shippingTitleValue,
  );
  const billingTitleLabel = getUserTitleLabelByValue(
    formatMessage,
    resolvedOrder.billingAddress?.country.code as
      | CountryCodeEnumType
      | undefined,
    billingTitleValue,
  );

  const preOrderData = extractPreorderData(resolvedOrder);
  const balance = extractBalance(resolvedOrder);

  const isBalanceCovered = balance.amount === 0;
  const isAuthorizationCoveringBalance =
    balance.amount > 0 &&
    resolvedOrder.totalAuthorized.amount >= balance.amount;
  const isFullyCovered = isBalanceCovered || isAuthorizationCoveringBalance;
  const isDepositPaid = isOrderDepositPaid(resolvedOrder);
  const isDeposit = isDepositPaid && !isFullyCovered;

  const orderLinesWithoutNulls = resolveOrderDetailsDisplayLines(
    resolvedOrder,
    state.fulfillment,
  );
  const sortedLines = sortLines(orderLinesWithoutNulls, true);

  const showStatusStepper: boolean = resolvedOrder.orderStatus
    ? [
        'IN_VALIDATION',
        'CONFIRMED',
        'IN_PREPARATION',
        'READY_TO_SHIP',
        'SHIPPED',
        'READY_FOR_COLLECTION',
        'COLLECTED',
      ]?.includes(resolvedOrder.orderStatus)
    : false;
  const taxMessage = getTaxMessageShort(resolvedOrder.channel.slug);

  const storeId = isHomeDelivery
    ? ''
    : (resolvedOrder.metadata.find(
        (item) => item.key === 'order.boutique_external_reference',
      )?.value ?? '');

  const userPhoneNumber = getContactPhone(resolvedOrder, user) ?? '';
  const voucherType = resolvedOrder.voucher?.discountValueType;

  const paymentRecords = (() => {
    const paymentMethod = state.paymentMethod;
    if (isEmpty(paymentMethod)) return null;
    const list = Array.isArray(paymentMethod) ? paymentMethod : [paymentMethod];
    return list as unknown as PaymentRecord[];
  })();

  const returnOrderCreatedDate =
    state.fulfillment?.returnCreatedAt?.split(' ')[0];

  const orderDetailsContextValue = useMemo<OrderDetailsContextValue>(
    () => ({
      billingTitleLabel,
      channelSlug,
      coSalesTax,
      deliveryMethodName,
      deliveryMode,
      formatMessage,
      fulfillment: state.fulfillment,
      guestSession,
      hasFulfillment: Boolean(state.fulfillment),
      imageLoader,
      isColoradoBilling,
      isDeposit,
      isHomeDelivery,
      isMobileView,
      isPayPerLinkTokenLoading,
      languageCode,
      order: resolvedOrder,
      orderLinesWithoutNulls,
      orderStatus: resolvedOrder.orderStatus as OrderAppOrderStatus,
      paths,
      payPerLinkToken,
      paymentMethod: state.paymentMethod,
      paymentRecords,
      preOrderData,
      push,
      returnOrderCreatedDate,
      salesTax,
      shippingTitleLabel,
      showStatusStepper,
      sortedLines,
      storeId,
      taxMessage,
      trackingLink: trackingLink ?? undefined,
      trackingNumber,
      type,
      userPhoneNumber,
      voucherType,
      withdrawal,
    }),
    [
      billingTitleLabel,
      channelSlug,
      coSalesTax,
      deliveryMethodName,
      deliveryMode,
      formatMessage,
      guestSession,
      imageLoader,
      isColoradoBilling,
      isDeposit,
      isHomeDelivery,
      isMobileView,
      isPayPerLinkTokenLoading,
      languageCode,
      orderLinesWithoutNulls,
      paths,
      payPerLinkToken,
      paymentRecords,
      preOrderData,
      push,
      returnOrderCreatedDate,
      salesTax,
      shippingTitleLabel,
      showStatusStepper,
      sortedLines,
      resolvedOrder,
      state.fulfillment,
      state.paymentMethod,
      storeId,
      taxMessage,
      trackingLink,
      trackingNumber,
      type,
      userPhoneNumber,
      voucherType,
      withdrawal,
    ],
  );

  return (
    <OrderDetailsProvider value={orderDetailsContextValue}>
      <OrderDetailsSections />
    </OrderDetailsProvider>
  );
}

export const OrderDetailsView = ({
  guestSession,
  order,
  orderId,
  paymentMethod,
  returnOrderId,
  type,
}: OrderDetailsViewProps) => {
  const { loader: imageLoader } = useImageLoader();
  const { paths, push } = useRouter();
  const {
    snapshot: { user },
  } = useUser();

  const {
    country: { channelSlug },
    query: { languageCode },
  } = useRegionProvider();
  const { deliveryModeTitleTranslation } = useTranslation();
  const isMobileView = useIsSmDown();
  const { formatMessage } = useIntl();
  const { IsTypeOrder } = useOrderIsColorado();
  const state = useOrderDetails({
    order,
    orderId,
    paymentMethod,
    returnOrderId,
    languageCode,
    user,
  });

  const orderChannelSlug = state.order?.channel?.slug;
  const { isOrderInWithdrawalScope, isScopeLoading: isWithdrawalScopeLoading } =
    useOrderWithdrawalScope(orderChannelSlug);

  const withdrawalScopeResolved =
    Boolean(orderChannelSlug) &&
    !isWithdrawalScopeLoading &&
    isOrderInWithdrawalScope;

  const {
    data: withdrawalEligibilityData,
    loading: withdrawalEligibilityQueryLoading,
  } = useStorefrontWithdrawalEligibilityQuery({
    variables: {
      input: {
        brand: BRAND.toUpperCase(),
        orderId: state.order?.id,
      },
    },
    skip:
      !WITHDRAWAL_ENABLED ||
      Boolean(guestSession) ||
      type === 'RETURNHISTORY' ||
      !!state.fulfillment ||
      !state.order?.id ||
      isWithdrawalScopeLoading ||
      !isOrderInWithdrawalScope,
    context: { client: APOLLO_CLIENTS.orderApp },
  });

  const { isPayPerLinkTokenLoading, payPerLinkToken } = usePaymentToken(
    state.order,
  );

  const orderLinesForWithdrawal = useMemo(
    () =>
      state.order
        ? resolveOrderDetailsDisplayLines(state.order, state.fulfillment)
        : [],
    [state.fulfillment, state.order],
  );

  const withdrawal = useOrderDetailsWithdrawal({
    formatMessage,
    guestSession,
    hasFulfillment: Boolean(state.fulfillment),
    isOrderInWithdrawalScope: withdrawalScopeResolved,
    orderLines: orderLinesForWithdrawal,
    type,
    withdrawalEligibilityData,
    withdrawalEligibilityQueryLoading,
  });

  if (!state.order || state.loading) {
    return <Loader data-testid='orderDetailsLoader' height={400} />;
  }

  const loadedState: OrderDetailsLoadedProps['state'] = {
    fulfillment: state.fulfillment,
    loading: state.loading,
    order: state.order,
    paymentMethod: state.paymentMethod,
  };

  return (
    <OrderDetailsLoaded
      IsTypeOrder={IsTypeOrder}
      channelSlug={channelSlug}
      deliveryModeTitleTranslation={deliveryModeTitleTranslation}
      formatMessage={formatMessage}
      guestSession={guestSession}
      imageLoader={imageLoader}
      isMobileView={isMobileView}
      isPayPerLinkTokenLoading={isPayPerLinkTokenLoading}
      languageCode={languageCode}
      paths={paths}
      payPerLinkToken={payPerLinkToken}
      push={push}
      state={loadedState}
      type={type}
      user={user}
      withdrawal={withdrawal}
    />
  );
};

OrderDetailsView.getLayout = (page: ReactElement) => {
  return (
    <DraftModeContext.Provider value={page.props.draftMode}>
      <MyOrdersLayout
        cmsData={page.props.cmsDefaultLayoutData}
        header={AM.myAccountMessages.orderDetail}
        withDetails
      >
        {page}
      </MyOrdersLayout>
    </DraftModeContext.Provider>
  );
};

OrderDetailsView.StoresSearch = StoresSearch;
